import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { POSITION_CANDIDATE_STATUSES } from "../../../../utils/constants/constants";

import { Autocomplete, Button, Dialog, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import DialogActions from "../../../../containers/DialogActions/DialogActions";

import { Props, State } from "./interfaces";
import { DialogContent } from "./styles";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { PositionStatusesApiService } from "../../../../services/recruiting/positions/position-statuses/position-statuses-api.service";
import { PositionSTO } from "../../../../models/recruiting/positions/position-sto";
import { PositionStatusRef } from "../../../../models/recruiting/positions/position-status/position-status-ref";
import { savePosition } from "../../../../services/recruiting/positions/positions-api.service";
import { CandidateRef } from "../../../../models/recruiting/candidate/candidate-ref";
import SuggestedCandidates from "./SuggestedCandidates/SuggestedCandidates";

const DEFAULT_POSITION: PositionSTO = {
  id: "",
  isDeleted: false,
  companyAccount: null,
  positionCode: "",
  hiringClient: null,
  position: "",
  openUntil: new Date(),
  activeRecruiters: [],
  clientContact: "",
  countries: null,
  isOpen: true,
  description: "",
  cVsSentToClientInProgress: [],
  cVsSentToClientDeclined: [],
  cVsInterviewedByClientInProgress: [],
  cVsInterviewedByClientDeclined: [],
  cVsInOfferInProgress: [],
  cVsInOfferDeclined: [],
  cVsHired: [],
  cVsOfferRejected: [],
  requiredSkills: [],
};

const EditCvStatus = ({
  children,
  candidate,
  currPosition,
  currCollection,
  currCollectionName,
  statusCollectionRef,
  currIndex,
  notifyParent,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.position_statuses";
  // const translationFields = `${tRP}.fields`;

  const [nameOfCollection, setNameOfCollection] = useState(currCollectionName);
  const [newCandidate, setNewCandidate] = useState<CandidateRef>();

  const currStatusCollectionRef = statusCollectionRef?.find(
    (cv) => cv.id === POSITION_CANDIDATE_STATUSES[nameOfCollection as keyof typeof POSITION_CANDIDATE_STATUSES]?.id
  );

  const selectedRowsChecked = useRef([]);

  const [state, setState] = useState<State>({
    openDialog: false,
    loading: false,
    error: "",
    position: { ...DEFAULT_POSITION },
    cvStatuses: null,
  });

  const setOpenDialog = (value: boolean) =>
    setState((state) => ({
      ...state,
      openDialog: value,
      cvStatuses: currStatusCollectionRef as PositionStatusRef,
    }));

  const handleCloseDialog = () => {
    setState((state) => ({
      ...state,
      error: "",
      openDialog: false,
      cvStatuses: null,
    }));
    setNameOfCollection(currCollectionName);
  };

  const handleChangeCvStatusField = (_: any, value: CandidateRef) => {
    const namecOfCurrCollection = Object.keys(POSITION_CANDIDATE_STATUSES).find(
      (key) => POSITION_CANDIDATE_STATUSES[key as keyof typeof POSITION_CANDIDATE_STATUSES].id === value?.id
    );

    setNameOfCollection(namecOfCurrCollection as string);
  };

  const handleChangeNewCandidateField = (_: any, value: CandidateRef) => {
    setNewCandidate(value);
  };

  const handleDelete = async () => {
    try {
      (currIndex === 0 || currIndex) && candidate && currCollection.splice(currIndex, 1);

      setNewCandidate(undefined);

      const { error } = await savePosition({
        ...state.position,
        [currCollectionName]: currCollection,
      });

      if (!error) {
        setState((state) => ({ ...state, openDialog: false }));
        notifyParent(true);
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  };

  const handleSaveCvStatus = async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      (currIndex === 0 || currIndex) && currCollection.splice(currIndex, 1);

      const currCandidate = candidate
        ? [candidate]
        : newCandidate
        ? [...selectedRowsChecked.current, newCandidate]
        : selectedRowsChecked.current;

      setNewCandidate(undefined);

      const { error } = await savePosition({
        ...state.position,
        [currCollectionName]: currCollection,
        [nameOfCollection as string]: [...(state.position as any)[nameOfCollection as string], ...currCandidate],
      });

      if (!error) {
        setState((state) => ({ ...state, openDialog: false }));
        notifyParent(true);
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  };

  const setCvStatus = useCallback(async () => {
    setState((state) => ({
      ...state,
      position: {
        ...currPosition,
      },
      cvStatuses: currStatusCollectionRef as PositionStatusRef,
    }));
  }, [currPosition]);

  useEffect(() => {
    setCvStatus();
    return () => {
      setState({
        openDialog: false,
        loading: false,
        error: "",
        position: { ...DEFAULT_POSITION },
        cvStatuses: null,
      });
    };
  }, [setCvStatus]);

  return (
    <>
      {children ? (
        <div onClick={() => setOpenDialog(true)}>{children}</div>
      ) : (
        <Button
          title={"Add suggested Candidates"}
          sx={{
            border: "1px solid #305E9B",
            minWidth: "16px",
            marginLeft: "10px",
            height: "16px",
            marginTop: "auto",
            marginBottom: "auto",
          }}
          onClick={() => setOpenDialog(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      )}
      <Dialog
        open={state.openDialog}
        onClose={handleCloseDialog}
        fullWidth
      >
        <DialogTitle
          title={children ? t(`${tRP}.title`) : t(`${tRP}.title_suggested_candidates`)}
          handleClose={handleCloseDialog}
        />
        <DialogContent>
          {candidate ? (
            <>
              <Autocomplete
                aria-required={true}
                disabled
                value={candidate}
                getOptionLabel={(option) => option.name}
                options={[candidate]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t(`${tRP}.fields.candidate`)}`}
                  />
                )}
              />
              <AutocompleteDropdown
                labelKey={t(`${tRP}.fields.cv_statuses`)}
                serviceApi={new PositionStatusesApiService()}
                propsItem={state}
                field="cvStatuses"
                handleChange={handleChangeCvStatusField}
              />
            </>
          ) : (
            <SuggestedCandidates
              positionId={currPosition.id}
              currPosition={currPosition}
              selectedRowsChecked={selectedRowsChecked}
              isOpen={!candidate}
              closeDialog={handleCloseDialog}
              saveCandidates={handleSaveCvStatus}
            />
          )}
        </DialogContent>
        <DialogActions
          handleCancel={handleCloseDialog}
          handleDelete={handleDelete}
          handleSave={handleSaveCvStatus}
          canBeDeleted={!!candidate}
          loading={state.loading}
          error={state.error}
        />
      </Dialog>
    </>
  );
};

export default EditCvStatus;
